import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { endpoints as metricsEndpoints } from '@/api/metrics';
import { API, endpoints, UpdateReportBody } from '@/api/reports';
import { selectAccountId } from '@/redux/session/selectors';
import type { Report } from '@/shared/types/reports';

export const useReport = (reportId: string | undefined) => {
  const accountId = useSelector(selectAccountId);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const reportKey = [endpoints.report(reportId ?? ''), accountId];

  const { data, status, error, isFetching, refetch } = useQuery<Report>({
    queryKey: reportKey,
    queryFn: () => API.getReport(reportId as string),
    enabled: !!accountId && !!reportId,
  });

  const {
    mutate: updateReport,
    mutateAsync: updateReportAsync,
    isPending: isUpdating,
  } = useMutation<Report, Error, UpdateReportBody>({
    mutationFn: (body) => API.updateReport(reportId as string, body),
    onSuccess: (updated) => {
      queryClient.setQueryData<Report>(reportKey, updated);
      queryClient.invalidateQueries({ queryKey: [endpoints.reports, accountId] });
      // Force a refetch of every metric chart/table in this report. New
      // metric content normally produces a new request hash (so a fresh fetch
      // happens automatically), but swapping metric types can collide with
      // an earlier cache entry — TanStack Query would then return stale data
      // first paint. Invalidating here guarantees the chart matches the
      // saved metric.
      queryClient.invalidateQueries({ queryKey: [metricsEndpoints.query, accountId] });
    },
    onError: (err) => toast.error(err.message || t('analytics.reports_detail_update_error')),
  });

  return {
    report: data,
    status,
    error,
    isFetching,
    refetch,
    updateReport,
    updateReportAsync,
    isUpdating,
  };
};
